import { Theme, useTheme } from "@mui/material/styles";
import MuiTab from "@mui/material/Tab";
import MuiTabs from "@mui/material/Tabs";
import { styled as muiStyled } from "@mui/system";
import { useEnvContext } from "context/EnvContext";
import { APP_PROVIDER_SEARCH, MESSENGER_TAB_INFO } from "core/consts";
import { isAppEmbedded } from "core/model/utils/featureFlags";
import { MessengerTabKinds, TourElement } from "core/types";
import { Dot } from "ds_legacy/components/Dot";
import {
  ACCENT_COLOR,
  CARD_BORDER,
  GREY_100,
  GREY_600,
} from "ds_legacy/materials/colors";
import {
  Z_INDEX_APP_BAR,
  border,
  dp,
  margin,
  padding,
} from "ds_legacy/materials/metrics";
import { shadow3dp } from "ds_legacy/materials/shadows";
import {
  CSSProperties,
  ChangeEvent,
  Fragment,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";

export type MessengerTabsProps = {
  activeTab: MessengerTabKinds | null;
  isTablet?: boolean;
  position?: CSSProperties["position"];
  tabs: {
    component: ReactElement<any>;
    key: MessengerTabKinds;
    label: string;
    onClick?: (...args: any) => void;
    tour?: TourElement;
    withDot?: boolean;
  }[];
};

type TabStyleProps = { isTablet?: boolean; theme: Theme };

const NOT_FOUND = -1;
const DEFAULT_TAB = 0;

export const TabContentWrapper = styled.div<{
  height?: CSSProperties["height"];
  overflowY?: CSSProperties["overflowY"];
}>`
  box-sizing: border-box;
  padding: ${padding(1)};
  width: 100%;
  overflow-y: ${({ overflowY }) => overflowY || "visible"};
  height: ${({ height }) => height || "100%"};
  & > * + * {
    margin: ${margin(4, 0, 0, 0)};
  }
`;

const getTabsHeight = (isTablet: TabStyleProps["isTablet"]) => ({
  height: isTablet ? "var(--tabs-height-mobile)" : "var(--tabs-height)",
  minHeight: `${
    isTablet ? "var(--tabs-height-mobile)" : "var(--tabs-height)"
  } !important`,
});

export function useMessengerTabs() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] =
    useState<MessengerTabKinds>(MESSENGER_TAB_INFO);

  useEffect(() => {
    const searchParamTab = searchParams.get("activeTab");
    if (searchParamTab && activeTab !== searchParamTab) {
      setActiveTab(searchParamTab as MessengerTabKinds);
    }
  }, [searchParams.get("activeTab")]);

  return { activeTab, setActiveTab };
}

const getBorderStyle = (
  isTablet: boolean,
): {
  border: CSSProperties["border"];
  borderBottom?: CSSProperties["borderBottom"];
  margin?: CSSProperties["margin"];
} => {
  if (!isTablet) {
    return {
      border: border({ color: CARD_BORDER }),
    };
  }

  return { border: undefined };
};

const StyledTabs = muiStyled(MuiTabs, {
  shouldForwardProp: (prop) =>
    typeof prop === "string" &&
    !["isTablet", "activeTab", "position", "appBarHeight"].includes(prop),
})<{
  activeTab: MessengerTabsProps["activeTab"];
  appBarHeight: number;
  isTablet: boolean | undefined;
  position?: CSSProperties["position"];
}>(({ appBarHeight, isTablet, position, theme }) => {
  const computedPosition = position || isTablet ? "fixed" : "static";
  const isEmbedded = isAppEmbedded();
  const computedTop =
    position === "static"
      ? 0
      : isTablet
      ? isEmbedded
        ? "0" // When embedded in iframe, position at top (no navbar)
        : appBarHeight === 82
        ? "var(--app-bar-providersearch-height)"
        : "var(--navbar-height)"
      : undefined;

  return {
    ...getTabsHeight(isTablet),
    background: isTablet ? theme.palette.primary.dark : GREY_100,
    boxShadow: isTablet ? shadow3dp : "none",
    boxSizing: "border-box",
    maxWidth: "100vw",
    position: computedPosition,
    top: computedTop,
    width: isTablet ? "100%" : "inherit",
    zIndex: Z_INDEX_APP_BAR - 1,
    ...getBorderStyle(!!isTablet),
    ".MuiTabs-scroller": {
      marginBottom: "-1px !important",
    },
    "&.MuiTabs-root": {
      overflow: "visible",
    },
  };
});

const StyledTab = muiStyled(MuiTab, {
  shouldForwardProp: (prop) =>
    typeof prop === "string" && !["isTablet"].includes(prop),
})<{ isTablet: boolean | undefined }>(({ isTablet, theme }) => ({
  ...getTabsHeight(isTablet),
  "&.Mui-selected": {
    background: isTablet ? "transparent" : theme.palette.common.white,
    color: isTablet ? theme.palette.common.white : theme.palette.primary.main,
    fontWeight: "bold !important",
  },
  "&.MuiTab-iconWrapper": {
    display: "flex",
    flexDirection: "row-reverse !important" as CSSProperties["flexDirection"], // needed due to https://github.com/cssinjs/jss/issues/1344
  },
  "&.MuiTab-labelIcon": {
    paddingTop: `${dp(6)} !important`,
  },
  color: isTablet ? theme.palette.common.white : GREY_600,
  textTransform: "capitalize",
}));

export function MessengerTabs({
  activeTab,
  isTablet,
  position,
  tabs = [],
}: MessengerTabsProps) {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabIndex = tabs.findIndex((tab) => tab.key === activeTab); // findIndex returns -1, if not found
  const [selectedTab, setSelectedTab] = useState(
    activeTabIndex === NOT_FOUND ? DEFAULT_TAB : activeTabIndex,
  );
  const { app } = useEnvContext();

  const handleTabChange = (event: ChangeEvent<AnyObject>, newValue: number) => {
    const activeTab = tabs[newValue].key;
    if (activeTab) {
      searchParams.set("activeTab", activeTab);
      setSearchParams(searchParams);
    }
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (selectedTab !== activeTabIndex) {
      setSelectedTab(
        activeTabIndex === NOT_FOUND ? DEFAULT_TAB : activeTabIndex,
      );
    }
  }, [activeTabIndex]);

  return (
    <>
      <StyledTabs
        activeTab={activeTab}
        appBarHeight={app === APP_PROVIDER_SEARCH ? 82 : 84}
        centered
        isTablet={isTablet}
        position={position}
        TabIndicatorProps={{
          style: {
            background: isTablet
              ? theme.palette.common.white
              : theme.palette.primary.main,
          },
        }}
        textColor="primary"
        value={selectedTab}
        variant="fullWidth"
        onChange={handleTabChange}
      >
        {tabs.map(({ key, label, onClick, tour, withDot }, index) => (
          <StyledTab
            key={index}
            data-testid={`${key}-tab`}
            disabled={tabs.length === 1}
            disableRipple
            icon={
              withDot ? (
                <Dot
                  color={ACCENT_COLOR}
                  margin={margin(0, 0, 0, 1)}
                  padding={padding(0.5)}
                />
              ) : undefined
            }
            iconPosition="end"
            isTablet={isTablet}
            label={
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                }}
              >
                {label}
              </div>
            }
            onClick={onClick}
            {...tour}
          />
        ))}
      </StyledTabs>
      <div
        style={{
          borderTop: "0px",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          overflow: "visible",
          padding: isTablet ? `var(--tabs-height-mobile) 0 0 0` : "auto",
        }}
      >
        {tabs.map(
          ({ component }, index) =>
            selectedTab === index && (
              <Fragment key={index}>{component}</Fragment>
            ),
        )}
      </div>
    </>
  );
}
